import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import productData, {
  shopCategories,
  siteConfig,
} from "../Datas/productData";
import { CartContext } from "./CartContext";

const occasionLinks = [
  { label: "Birthday", query: "birthday" },
  { label: "Thank You", query: "thank you" },
  { label: "Love & Friendship", query: "love" },
  { label: "New Baby", query: "new baby" },
  { label: "Congratulations", query: "congratulations" },
  { label: "Just Because", query: "just because" },
  { label: "For Teachers", query: "encouragement" },
  { label: "Holiday", query: "celebration" },
];

const cardCollectionLinks = [
  { label: "All greeting cards", to: "/shop?category=Greeting%20Cards" },
  { label: "Scratch-off cards", to: "/shop?tag=scratch-off" },
  { label: "Pop-up cards", to: "/shop?tag=pop-up" },
  { label: "Interactive cards", to: "/shop?tag=interactive" },
];

const normalizeCategory = (category) => {
  if (typeof category === "string") {
    return { label: category, value: category };
  }

  const label =
    category?.label || category?.name || category?.title || category?.slug;

  return {
    label,
    value: category?.value || category?.slug || label,
  };
};

const fallbackCategories = Array.from(
  new Set(productData.map((product) => product.category).filter(Boolean))
);
const productIds = new Set(productData.map((product) => product.id));

const categoryLinks = (
  Array.isArray(shopCategories) && shopCategories.length
    ? shopCategories
    : fallbackCategories
)
  .map(normalizeCategory)
  .filter((category) => category.label && category.value);

const categoryHref = (category) =>
  `/shop?category=${encodeURIComponent(category.value)}`;

function Header() {
  const { cartItems = [] } = useContext(CartContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const menuToggleRef = useRef(null);
  const mobileNavigationRef = useRef(null);
  const mobileCloseRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const brandName = siteConfig?.brandName || "Paperie";
  const announcement =
    siteConfig?.announcement || "Join our paper-loving list for 10% off";
  const flatRate =
    siteConfig?.shipping?.flatRate ?? siteConfig?.shipping?.standardRate ?? 5;
  const freeThreshold = siteConfig?.shipping?.freeThreshold ?? 50;
  const cartCount = cartItems.filter((item) => productIds.has(item.id)).reduce(
    (total, item) => total + (Number(item.quantity) || 1),
    0
  );
  const keepsakeCategories = categoryLinks.filter((category) =>
    /note|journal|sticker|pencil|gift|keepsake|stationery/i.test(category.label)
  );

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setIsSearchOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const menuButton = menuToggleRef.current;
    const focusSelector =
      'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
    const focusFrame = window.requestAnimationFrame(() =>
      mobileCloseRef.current?.focus()
    );

    document.body.style.overflow = "hidden";

    const keepFocusInDrawer = (event) => {
      if (event.key !== "Tab") return;
      const focusableElements = Array.from(
        mobileNavigationRef.current?.querySelectorAll(focusSelector) || []
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement || !lastElement) return;
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", keepFocusInDrawer);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keepFocusInDrawer);
      menuButton?.focus();
    };
  }, [isMenuOpen]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      searchInputRef.current?.focus();
      return;
    }

    navigate(`/shop?q=${encodeURIComponent(trimmedQuery)}`);
    setSearchQuery("");
  };

  const closeNavigation = () => {
    setIsMenuOpen(false);
    toTop();
  };

  return (
    <header className="site-header">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <div className="header-announcement">
        <span aria-hidden="true">—</span>
        <span>{announcement}</span>
        <span aria-hidden="true">—</span>
      </div>

      <div className="header-masthead">
        <div className="header-masthead__inner">
          <div className="header-tools header-tools--left">
            <button
              className="header-icon-button header-menu-toggle"
              ref={menuToggleRef}
              type="button"
              aria-controls="mobile-navigation"
              aria-expanded={isMenuOpen}
              aria-label="Open menu"
              onClick={() => setIsMenuOpen(true)}
            >
              <span className="header-menu-icon" aria-hidden="true">
                <span />
                <span />
              </span>
            </button>
            <div className="header-delivery" aria-label="Shipping offers">
              <span>${flatRate} flat-rate shipping</span>
              <span>Free U.S. shipping over ${freeThreshold}</span>
            </div>
          </div>

          <Link className="header-brand" to="/" onClick={toTop}>
            <span className="header-brand__mark" aria-hidden="true">
              {siteConfig.logo?.image ? (
                <img src={siteConfig.logo.image} alt="" />
              ) : (
                "✣"
              )}
            </span>
            <span>{siteConfig.logo?.text || brandName}</span>
          </Link>

          <div className="header-tools header-tools--right">
            <button
              className="header-text-button"
              type="button"
              aria-controls="site-search"
              aria-expanded={isSearchOpen}
              onClick={() => setIsSearchOpen((isOpen) => !isOpen)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="10.5" cy="10.5" r="6.5" />
                <path d="m15.5 15.5 5 5" />
              </svg>
              <span>Search</span>
            </button>
            <Link className="header-utility-link" to="/about" onClick={toTop}>
              Our story
            </Link>
            <Link
              className="header-utility-link"
              to="/contact"
              onClick={toTop}
            >
              Contact
            </Link>
            <Link
              className="header-cart-link"
              to="/cartpage"
              onClick={toTop}
              aria-label={`Cart with ${cartCount} item${cartCount === 1 ? "" : "s"}`}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 7h16l-1.2 13H5.2L4 7Z" />
                <path d="M9 9V6a3 3 0 0 1 6 0v3" />
              </svg>
              <span className="header-cart-link__label">Cart</span>
              <span className="header-cart-link__count">{cartCount}</span>
            </Link>
          </div>
        </div>
      </div>

      <nav className="desktop-navigation" aria-label="Primary navigation">
        <ul className="desktop-navigation__list">
          <li>
            <Link to="/shop?tag=new" onClick={toTop}>
              New arrivals
            </Link>
          </li>
          <li className="desktop-navigation__has-menu">
            <button type="button" aria-haspopup="true">
              Occasion <span aria-hidden="true">⌄</span>
            </button>
            <div className="mega-menu mega-menu--occasion">
              <div className="mega-menu__inner">
                <div className="mega-menu__intro">
                  <span className="mega-menu__eyebrow">Find the right note</span>
                  <h2>Cards for all of life&rsquo;s little moments.</h2>
                  <Link to="/shop" onClick={toTop}>
                    Shop every occasion <span aria-hidden="true">→</span>
                  </Link>
                </div>
                <div className="mega-menu__column">
                  <p>Occasion</p>
                  <ul>
                    {occasionLinks.slice(0, 4).map((occasion) => (
                      <li key={occasion.label}>
                        <Link
                          to={`/shop?q=${encodeURIComponent(occasion.query)}`}
                          onClick={toTop}
                        >
                          {occasion.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mega-menu__column">
                  <p>More moments</p>
                  <ul>
                    {occasionLinks.slice(4).map((occasion) => (
                      <li key={occasion.label}>
                        <Link
                          to={`/shop?q=${encodeURIComponent(occasion.query)}`}
                          onClick={toTop}
                        >
                          {occasion.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </li>
          <li className="desktop-navigation__has-menu">
            <button type="button" aria-haspopup="true">
              Greeting cards <span aria-hidden="true">⌄</span>
            </button>
            <div className="mega-menu">
              <div className="mega-menu__inner mega-menu__inner--categories">
                <div className="mega-menu__intro">
                  <span className="mega-menu__eyebrow">Paper with personality</span>
                  <h2>Small surprises, ready to send.</h2>
                  <Link to="/shop" onClick={toTop}>
                    Shop all paper goods <span aria-hidden="true">→</span>
                  </Link>
                </div>
                <div className="mega-menu__column">
                  <p>Collections</p>
                  <ul>
                    {cardCollectionLinks.map((collection) => (
                      <li key={collection.label}>
                        <Link to={collection.to} onClick={toTop}>
                          {collection.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mega-menu__column">
                  <p>Browse</p>
                  <ul>
                    <li>
                      <Link to="/shop?sort=best-selling" onClick={toTop}>
                        Best sellers
                      </Link>
                    </li>
                    <li>
                      <Link to="/shop?tag=new" onClick={toTop}>
                        New &amp; noteworthy
                      </Link>
                    </li>
                    <li>
                      <Link to="/shop?price=under-15" onClick={toTop}>
                        Little gifts under $15
                      </Link>
                    </li>
                    <li>
                      <Link to="/about" onClick={toTop}>
                        Made with care
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </li>
          <li className="desktop-navigation__has-menu">
            <button type="button" aria-haspopup="true">
              Gifts &amp; keepsakes <span aria-hidden="true">⌄</span>
            </button>
            <div className="mega-menu mega-menu--compact">
              <div className="mega-menu__inner mega-menu__inner--compact">
                <div className="mega-menu__intro">
                  <span className="mega-menu__eyebrow">Useful, joyful, thoughtful</span>
                  <h2>Desk treats and keepsakes to hold onto.</h2>
                </div>
                <div className="mega-menu__column mega-menu__column--wide">
                  <p>Shop gifts</p>
                  <ul className="mega-menu__link-grid">
                    {(keepsakeCategories.length
                      ? keepsakeCategories
                      : categoryLinks
                    )
                      .slice(0, 8)
                      .map((category) => (
                        <li key={category.value}>
                          <Link to={categoryHref(category)} onClick={toTop}>
                            {category.label}
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </li>
          <li>
            <Link to="/shop?tag=kids" onClick={toTop}>
              For kids
            </Link>
          </li>
          <li>
            <Link to="/shop?sale=true" onClick={toTop}>
              Sale
            </Link>
          </li>
        </ul>
      </nav>

      <div
        className={`header-search ${isSearchOpen ? "is-open" : ""}`}
        id="site-search"
      >
        <form className="header-search__form" role="search" onSubmit={handleSearch}>
          <label htmlFor="site-search-input">What are you looking for?</label>
          <div className="header-search__controls">
            <input
              id="site-search-input"
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              placeholder="Search cards, notes and little gifts"
              autoComplete="off"
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <button className="header-search__submit" type="submit">
              Search
            </button>
            <button
              className="header-search__close"
              type="button"
              aria-label="Close search"
              onClick={() => setIsSearchOpen(false)}
            >
              ×
            </button>
          </div>
        </form>
      </div>

      <button
        className={`header-drawer-scrim ${isMenuOpen ? "is-open" : ""}`}
        type="button"
        aria-label="Close menu"
        tabIndex={isMenuOpen ? 0 : -1}
        onClick={() => setIsMenuOpen(false)}
      />

      <aside
        className={`mobile-navigation ${isMenuOpen ? "is-open" : ""}`}
        ref={mobileNavigationRef}
        id="mobile-navigation"
        aria-hidden={!isMenuOpen}
        aria-label="Mobile navigation"
      >
        <div className="mobile-navigation__header">
          <span className="mobile-navigation__brand">{brandName}</span>
          <button
            type="button"
            ref={mobileCloseRef}
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            ×
          </button>
        </div>
        <div className="mobile-navigation__body">
          <Link to="/shop?tag=new" onClick={closeNavigation}>
            New arrivals
          </Link>
          <details>
            <summary>Shop by occasion</summary>
            <div className="mobile-navigation__submenu">
              {occasionLinks.map((occasion) => (
                <Link
                  key={occasion.label}
                  to={`/shop?q=${encodeURIComponent(occasion.query)}`}
                  onClick={closeNavigation}
                >
                  {occasion.label}
                </Link>
              ))}
            </div>
          </details>
          <details>
            <summary>Shop paper goods</summary>
            <div className="mobile-navigation__submenu">
              {categoryLinks.map((category) => (
                <Link
                  key={category.value}
                  to={categoryHref(category)}
                  onClick={closeNavigation}
                >
                  {category.label}
                </Link>
              ))}
            </div>
          </details>
          <Link to="/shop?tag=kids" onClick={closeNavigation}>
            For kids
          </Link>
          <Link to="/shop?sale=true" onClick={closeNavigation}>
            Sale
          </Link>
          <div className="mobile-navigation__secondary">
            <Link to="/about" onClick={closeNavigation}>
              Our story
            </Link>
            <Link to="/contact" onClick={closeNavigation}>
              Contact
            </Link>
            <Link to="/cartpage" onClick={closeNavigation}>
              Cart ({cartCount})
            </Link>
          </div>
        </div>
      </aside>
    </header>
  );
}

export default Header;
